import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { isWorkspaceRoute } from '@/config/routes.config';

const STORAGE_KEY = 'smarty:sidebar-collapsed';
const HTML_CLASS = 'sidebar-collapsed';
const HTML_RAIL_AUTO_CLASS = 'sidebar-rail-auto';

function readInitial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Manages sidebar collapsed state.
 * - Manual toggle (Ctrl+B / hamburger) persists to localStorage.
 * - CSS class on <html> drives all visuals without re-rendering Frame.
 */
export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(readInitial);
  const location = useLocation();

  const isWorkspace = useMemo(
    () => isWorkspaceRoute(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    const el = document.documentElement;
    el.classList.toggle(HTML_CLASS, collapsed);
    el.classList.toggle(HTML_RAIL_AUTO_CLASS, isWorkspace);
  }, [collapsed, isWorkspace]);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch { /* quota */ }
      return next;
    });
  }, []);

  // Keyboard shortcut: Ctrl+B (VS Code / Notion style)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'b' && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return { collapsed, isWorkspace, toggle } as const;
}
